const BLOCK_ENVIRONMENTS = [
  'equation',
  'align',
  'eqnarray',
  'multline',
  'gather',
];

const INLINE_MATH_COMMANDS = [
  'frac', 'sqrt', 'int', 'sum', 'prod', 'lim', 'log', 'ln', 'sin', 'cos',
  'tan', 'alpha', 'beta', 'gamma', 'delta', 'epsilon', 'theta', 'lambda',
  'mu', 'pi', 'sigma', 'omega', 'phi', 'psi', 'chi', 'rho', 'tau',
  'infty', 'partial', 'nabla', 'times', 'div', 'pm', 'mp', 'cdot',
  'leq', 'geq', 'neq', 'approx', 'equiv', 'leftarrow', 'rightarrow',
  'text', 'mathrm', 'mathbf', 'mathit', 'mathcal', 'mathbb', 'overline',
  'underline', 'hat', 'bar', 'vec', 'dot', 'ddot', 'tilde', 'binom',
];

const PROTECTED_MARKDOWN_PATTERN = /(```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`)/g;

const BARE_MATH_COMMAND_PATTERN = new RegExp(
  `\\\\(?:${INLINE_MATH_COMMANDS.join('|')})(?![a-zA-Z])`
);

const BLOCK_ENVIRONMENT_PATTERN = new RegExp(
  `^\\\\begin\\{(${BLOCK_ENVIRONMENTS.join('|')})(\\*?)\\}`
);

const isEscaped = (text, index) => {
  let backslashes = 0;
  for (let i = index - 1; i >= 0 && text[i] === '\\'; i -= 1) {
    backslashes += 1;
  }
  return backslashes % 2 === 1;
};

const findUnescaped = (text, token, from) => {
  let index = text.indexOf(token, from);
  while (index !== -1 && isEscaped(text, index)) {
    index = text.indexOf(token, index + 1);
  }
  return index;
};

const BLANK_LINE_PATTERN = /\n[ \t>]*\n/;

/**
 * Split a markdown segment into plain text and math spans.
 * Unclosed delimiters stay plain text so partial or literal usage is untouched.
 */
const splitMathSpans = (segment) => {
  const parts = [];
  let textStart = 0;
  let i = 0;

  const pushSpan = (kind, start, end, content) => {
    if (start > textStart) {
      parts.push({ kind: 'text', value: segment.slice(textStart, start), start: textStart });
    }
    parts.push({ kind, value: segment.slice(start, end), content, start });
    textStart = end;
    i = end;
  };

  while (i < segment.length) {
    const char = segment[i];
    if (char === '$' && !isEscaped(segment, i)) {
      if (segment[i + 1] === '$') {
        const close = findUnescaped(segment, '$$', i + 2);
        if (close !== -1) {
          pushSpan('dollarDisplay', i, close + 2, segment.slice(i + 2, close));
          continue;
        }
        i += 2;
        continue;
      }
      const lineEnd = segment.indexOf('\n', i);
      const close = findUnescaped(segment, '$', i + 1);
      if (close > i + 1 && (lineEnd === -1 || close < lineEnd)) {
        pushSpan('dollarInline', i, close + 1, segment.slice(i + 1, close));
        continue;
      }
    } else if (char === '\\' && !isEscaped(segment, i)) {
      const next = segment[i + 1];
      if (next === '[' || next === '(') {
        const closeToken = next === '[' ? '\\]' : '\\)';
        const close = findUnescaped(segment, closeToken, i + 2);
        const content = close === -1 ? '' : segment.slice(i + 2, close);
        const valid = close !== -1
          && content.trim() !== ''
          && (next === '[' || !BLANK_LINE_PATTERN.test(content));
        if (valid) {
          pushSpan(next === '[' ? 'bracketDisplay' : 'parenInline', i, close + 2, content);
          continue;
        }
      } else if (next === 'b') {
        const match = segment.slice(i).match(BLOCK_ENVIRONMENT_PATTERN);
        if (match) {
          const endToken = `\\end{${match[1]}${match[2]}}`;
          const close = segment.indexOf(endToken, i + match[0].length);
          if (close !== -1) {
            pushSpan(
              'environmentDisplay',
              i,
              close + endToken.length,
              segment.slice(i + match[0].length, close),
            );
            continue;
          }
        }
      }
    }
    i += 1;
  }

  if (textStart < segment.length) {
    parts.push({ kind: 'text', value: segment.slice(textStart), start: textStart });
  }
  return parts;
};

const normalizeMathContent = (content) => {
  let result = content.replace(
    /\\begin\{cases\}([\s\S]*?)\\end\{cases\}/g,
    (_, inner) => {
      const fixedContent = inner
        .replace(/\s+\\\s+/g, ' \\\\ ')
        .replace(/\s+\\\\\s+/g, ' \\\\ ')
        .replace(/\s*\\\\\s*$/g, '')
        .trim();
      return `\\begin{cases}${fixedContent}\\end{cases}`;
    }
  );
  if (result.includes('\\int')) {
    result = result.replace(/(^|[^\\]),\s*d([a-z])\b/g, '$1\\, d$2');
  }
  return result
    .replace(/\^(\d{2,})/g, '^{$1}')
    .replace(/_(\d{2,})/g, '_{$1}');
};

const CONTAINER_PREFIX_PATTERN = /^([ \t>]*)((?:[-*+]|\d+[.)])[ \t]+)?/;

const stripContainerPrefix = (line, quoteDepth) => {
  if (quoteDepth === 0) return line.trim();
  const quotePattern = new RegExp(`^(?:[ \\t]*>){0,${quoteDepth}}`);
  return line.replace(quotePattern, '').trim();
};

/**
 * Emit display math as a `$$` fence that stays inside the surrounding
 * list item or blockquote, so remark-math sees one well-formed block.
 */
const renderDisplayMath = (segment, part, content) => {
  const lineStart = segment.lastIndexOf('\n', part.start - 1) + 1;
  const lead = segment.slice(lineStart, part.start);
  const containerMatch = lead.match(CONTAINER_PREFIX_PATTERN);
  const leadIsContainerOnly = /^[ \t>]*$/.test(lead);
  const indent = leadIsContainerOnly
    ? lead
    : containerMatch[1] + ' '.repeat((containerMatch[2] || '').length);
  const quoteDepth = (indent.match(/>/g) || []).length;

  const body = normalizeMathContent(content)
    .split('\n')
    .map((line) => stripContainerPrefix(line, quoteDepth))
    .filter((line) => line !== '');
  if (body.length === 0) return part.value;

  const after = segment.slice(part.start + part.value.length);
  const opening = leadIsContainerOnly ? '' : `\n${indent}`;
  const closing = /^[ \t]*(\n|$)/.test(after) ? '' : `\n${indent}`;
  return `${opening}$$\n${body.map((line) => `${indent}${line}`).join('\n')}\n${indent}$$${closing}`;
};

const renderInlineMath = (part, content) => {
  const inner = content.replace(/\s*\n\s*/g, ' ').trim();
  if (inner.includes('$')) return part.value;
  return `$${normalizeMathContent(inner)}$`;
};

const BARE_LINE_PREFIX_PATTERN = /^([ \t]*(?:>[ \t]*)*(?:(?:[-*+]|\d+[.)])[ \t]+)?)/;

const wrapBareMathLine = (line) => {
  if (line.includes('$') || !BARE_MATH_COMMAND_PATTERN.test(line)) return line;
  if (/^[ \t]*\|/.test(line)) return line;
  const prefix = line.match(BARE_LINE_PREFIX_PATTERN)[1];
  const body = line.slice(prefix.length).trim();
  return body ? `${prefix}$$${normalizeMathContent(body)}$$` : line;
};

/**
 * Only lines that contain no math span at all are candidates for wrapping.
 * The first/last fragment of a text part shares its line with a math span.
 */
const renderTextPart = (value, isFirstPart, isLastPart) => {
  const lines = value.split('\n');
  return lines
    .map((line, index) => {
      const startsLine = index > 0 || isFirstPart;
      const endsLine = index < lines.length - 1 || isLastPart;
      return startsLine && endsLine ? wrapBareMathLine(line) : line;
    })
    .join('\n');
};

const normalizeMathSegment = (segment) => {
  const parts = splitMathSpans(segment);
  return parts
    .map((part, index) => {
      switch (part.kind) {
        case 'text':
          return renderTextPart(part.value, index === 0, index === parts.length - 1);
        case 'dollarInline':
          return `$${normalizeMathContent(part.content)}$`;
        case 'dollarDisplay':
          return `$$${normalizeMathContent(part.content)}$$`;
        case 'parenInline':
          return renderInlineMath(part, part.content);
        case 'bracketDisplay':
        case 'environmentDisplay':
          return renderDisplayMath(segment, part, part.content);
        default:
          return part.value;
      }
    })
    .join('');
};

/**
 * Normalize explicit LaTeX syntax without mutating fenced/inline code.
 *
 * Text outside math spans is left untouched except for lines that contain
 * a bare LaTeX command. Plain `1/2`, dates, and URL path segments remain text.
 */
export const preprocessMarkdownMath = (rawContent) => {
  if (!rawContent) return '';
  return String(rawContent)
    .split(PROTECTED_MARKDOWN_PATTERN)
    .map((segment, index) => (
      index % 2 === 1 ? segment : normalizeMathSegment(segment)
    ))
    .join('');
};
